import { defineTour } from '../../types';
import { PERMISSIONS, canAll } from './access';

/**
 * T02 — Find items and prepare a request (brief §7).
 *
 * For a seat that may browse the catalogue AND raise a request, as the
 * catalogue's Add control and the Cart entry are (`AddToRequest`, AppLayout's
 * Cart item). The catalogue part navigates to `/catalogue` (GET only). The
 * cart part never navigates: `/cart` validates on opening and saves fields on
 * blur (safeRoutes: EXCLUDED), so those steps continue only once the person
 * has opened the cart themselves. The guide ends ON "Review & submit": the
 * review step is where a real request is submitted, and the person does that.
 *
 * Copy: `guidance.tours.T02.*` — draft, for native-language review.
 */
export const T02 = defineTour({
  id: 'T02',
  version: 1,
  portal: 'tenant',
  titleKey: 'guidance.tours.T02.title',
  summaryKey: 'guidance.tours.T02.summary',
  eligible: (ctx) =>
    ctx.portal === 'tenant' && canAll(ctx, PERMISSIONS.catalogueBrowse, PERMISSIONS.requestCreate),
  pageRoutes: ['tenant.catalogue', 'tenant.cart'],
  steps: [
    {
      id: 't02-search',
      anchor: 'tenant.catalogue.filters',
      route: 'tenant.catalogue',
      titleKey: 'guidance.tours.T02.search.title',
      bodyKey: 'guidance.tours.T02.search.body',
    },
    {
      // The first orderable item's facts (never a record the guide picked:
      // the page tags whichever item comes first).
      id: 't02-item-facts',
      anchor: 'tenant.catalogue.item-facts',
      titleKey: 'guidance.tours.T02.itemFacts.title',
      bodyKey: 'guidance.tours.T02.itemFacts.body',
    },
    {
      // Highlighted only; the guide never adds anything.
      id: 't02-add',
      anchor: 'tenant.catalogue.add',
      titleKey: 'guidance.tours.T02.add.title',
      bodyKey: 'guidance.tours.T02.add.body',
    },
    {
      id: 't02-off-catalogue',
      anchor: 'tenant.catalogue.off-catalogue',
      when: (ctx) => ctx.can(PERMISSIONS.requestOffCatalogue),
      titleKey: 'guidance.tours.T02.offCatalogue.title',
      bodyKey: 'guidance.tours.T02.offCatalogue.body',
    },
    {
      id: 't02-cart-lines',
      anchor: 'tenant.cart.lines',
      needsRecord: { routeId: 'tenant.cart', explainKey: 'guidance.tours.T02.openCart' },
      titleKey: 'guidance.tours.T02.cartLines.title',
      bodyKey: 'guidance.tours.T02.cartLines.body',
    },
    {
      id: 't02-cart-details',
      anchor: 'tenant.cart.details',
      needsRecord: { routeId: 'tenant.cart', explainKey: 'guidance.tours.T02.openCart' },
      titleKey: 'guidance.tours.T02.cartDetails.title',
      bodyKey: 'guidance.tours.T02.cartDetails.body',
    },
    {
      id: 't02-review',
      anchor: 'tenant.cart.review',
      needsRecord: { routeId: 'tenant.cart', explainKey: 'guidance.tours.T02.openCart' },
      titleKey: 'guidance.tours.T02.review.title',
      bodyKey: 'guidance.tours.T02.review.body',
      commitNote: true,
    },
  ],
});
